/* eslint-disable react/prop-types */
import { Select } from 'antd';
import { ComparisonOperator, FilterColumn, GetOperatorsForType, getOperatorsForType } from './filters.types';

import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OperatorList, TRule, TRuleValue } from './filters.types';
import { RuleValueControlPicker } from './helpers/RuleValueControlEvaulator';
type RuleInputs = {
  columns: FilterColumn[];
  onChange: (filters: TRule) => void;
  intRule: TRule;
};

export const AdvancedRule: React.FC<RuleInputs> = React.memo(({ columns, onChange, intRule }) => {
  const { t } = useTranslation();
  const selectedColumn = useMemo(() => {
    return columns.find((d) => d.name === intRule.col);
  }, [columns, intRule.col]);

  const availableOperators = useMemo((): OperatorList => {
    if (!selectedColumn) return [];
    return getOperatorsForType(selectedColumn, t);
  }, [selectedColumn, t]);

  const columnOptions = useMemo(() => {
    return columns.map((d) => ({
      value: d.name,
      label: d.label,
    }));
  }, [columns]);

  const onColumnSelect = useCallback(
    (columnname: string) => {
      const col = columns.find((d) => d.name === columnname);
      if (!col) return;

      // Determine default operator based on column type
      let defaultOperator: ComparisonOperator;
      if (col.type === 'boolean') {
        defaultOperator = '==';
      } else if (col.type === 'text') {
        defaultOperator = 'contains'; // Default to 'contains' for text fields
      } else {
        defaultOperator = GetOperatorsForType(col)[0]?.value || ('equals' as ComparisonOperator);
      }

      const updatedRule: TRule = {
        ...intRule,
        col: columnname,
        operator: defaultOperator,
        value: col.type === 'boolean' ? 1 : null, // Set to 1 (true) for boolean fields
      };

      onChange(updatedRule);
    },
    [columns, intRule, onChange]
  );

  const onOperatorChange = useCallback(
    (operator: ComparisonOperator) => {
      const updatedRule: TRule = {
        ...intRule,
        operator,
      };
      onChange(updatedRule);
    },
    [intRule, onChange]
  );

  const onValueChange = useCallback(
    (value: TRuleValue) => {
      const updatedRule: TRule = {
        ...intRule,
        value,
      };
      onChange(updatedRule);
    },
    [intRule, onChange]
  );

  // For boolean fields, handle combined operator+value selection
  const onBooleanOptionChange = useCallback(
    (combinedValue: string) => {
      // combinedValue format: "is" or "is_not"
      const updatedRule: TRule = {
        ...intRule,
        operator: combinedValue === 'is' ? '==' : '!=',
        value: 1, // Always set to 1 (true) since the operator handles the logic
      };
      onChange(updatedRule);
    },
    [intRule, onChange]
  );

  // Determine the current boolean option based on operator
  const currentBooleanOption = useMemo(() => {
    if (selectedColumn?.type === 'boolean') {
      return intRule.operator === '!=' ? 'is_not' : 'is';
    }
    return undefined;
  }, [selectedColumn?.type, intRule.operator]);

  return (
    <div className="flex w-full">
      <div className="w-[30%] mr-2">
        <Select
          className="w-full h-8"
          showSearch
          placeholder={t('filters-select-column')}
          optionFilterProp="label"
          value={intRule.col || undefined}
          onChange={onColumnSelect}
          options={columnOptions}
        />
      </div>
      {selectedColumn?.type && selectedColumn.type !== 'boolean' && (
        <div className="w-[18%] mr-2">
          <Select
            className="w-full h-8"
            showSearch
            optionFilterProp="label"
            value={intRule.operator}
            onChange={onOperatorChange}
            options={availableOperators}
          />
        </div>
      )}
      {selectedColumn?.type === 'boolean' ? (
        <div className="w-[70%] mr-2">
          <Select
            className="w-full h-8"
            placeholder={t('filters-select-option')}
            value={currentBooleanOption}
            onChange={onBooleanOptionChange}
            options={[
              { label: t('filters-bool-is', { label: selectedColumn.label }), value: 'is' },
              { label: t('filters-bool-is-not', { label: selectedColumn.label }), value: 'is_not' },
            ]}
          />
        </div>
      ) : (
        selectedColumn?.type && (
          <div className="w-[52%] mr-2">
            <RuleValueControlPicker col={selectedColumn} value={intRule.value} onChange={onValueChange} />
          </div>
        )
      )}
    </div>
  );
});

AdvancedRule.displayName = 'AdvancedRule';
